import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import { isAuthed } from "./services/teacherApi";
import { LightboxProvider } from "./components/ImageLightbox";

// Not routed (handled in the admin panel instead), but kept in the tree so
// restoring one is a lazy import, a <Route> and a SideMenu entry:
// pages/QuestionRegistryPage.tsx, PasswordRequestsPage.tsx,
// ParentApprovalsPage.tsx and NoticesPage.tsx (staff notices), plus their
// blocks in services/teacherApi.ts.
// Route-level code-splitting. Login + Dashboard (the two entry screens) ship in
// the initial bundle so there's no spinner flash on open. Every other page loads
// its own chunk on first navigation — crucially the heavy ones: AttemptPage
// (KaTeX + react-markdown) and QuestionRegistryPage (recharts) no longer weigh
// down first paint for teachers who never open them in a given session.
const LibraryPage = lazy(() => import("./pages/LibraryPage"));
const CreatePage = lazy(() => import("./pages/CreatePage"));
const SubmissionsPage = lazy(() => import("./pages/SubmissionsPage"));
const AttemptPage = lazy(() => import("./pages/AttemptPage"));
const PreviewPage = lazy(() => import("./pages/PreviewPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const AttendancePage = lazy(() => import("./pages/AttendancePage"));
const AttendanceHistoryPage = lazy(() => import("./pages/AttendanceHistoryPage"));
const LeavesPage = lazy(() => import("./pages/LeavesPage"));
const AdminCheckInsPage = lazy(() => import("./pages/AdminCheckInsPage"));
const ProgressPage = lazy(() => import("./pages/ProgressPage"));
const PeriodLogPage = lazy(() => import("./pages/PeriodLogPage"));
const SchoolProgressPage = lazy(() => import("./pages/SchoolProgressPage"));
// Routine builder (admin/HoD). Its own chunk — plain teachers never open it.
const RoutinePage = lazy(() => import("./pages/RoutinePage"));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));
const AnnouncementsPage = lazy(() => import("./pages/AnnouncementsPage"));
const StudentParticipationPage = lazy(() => import("./pages/StudentParticipationPage"));
// Student attendance — the daily class register a teacher marks, and the
// printable register that comes out of it. Distinct from AttendancePage above,
// which is the teacher's own geofenced punch clock.
const StudentAttendancePage = lazy(() => import("./pages/StudentAttendancePage"));
const StudentAttendanceRegisterPage = lazy(() => import("./pages/StudentAttendanceRegisterPage"));
// RFID gate cards + readers (office only — the page redirects anyone else).
const RfidCardsPage = lazy(() => import("./pages/RfidCardsPage"));
// The per-student record — profile/medical, contacts, infirmary, discipline,
// complaints, marks and doubts. Its own chunk: six tabs' worth of forms that a
// teacher only pays for when they open a student.
const StudentDossierPage = lazy(() => import("./pages/student/StudentDossierPage"));
// Offline exam marks — the to-do list of papers and the roster sheet. Its own
// chunk: a teacher only pays for it during an exam week.
const ExamMarksPage = lazy(() => import("./pages/ExamMarksPage"));
const ExamSheetPage = lazy(() => import("./pages/ExamMarksPage").then((m) => ({ default: m.ExamSheetPage })));
const DoubtsListPage = lazy(() => import("./pages/DoubtsListPage"));
const DoubtThreadPage = lazy(() => import("./pages/DoubtThreadPage"));
const ComplaintsRegistryPage = lazy(() => import("./pages/ComplaintsRegistryPage"));
const ComplaintCreatePage = lazy(() => import("./pages/ComplaintCreatePage"));
// Parent doubts — the staff end of "Ask a teacher" in the Parent app. Its own
// chunk; a teacher pays for it only when a parent has actually written.
const ParentDoubtsListPage = lazy(() => import("./pages/ParentDoubtsListPage"));
const ParentDoubtThreadPage = lazy(() => import("./pages/ParentDoubtThreadPage"));
// Parent complaints — the family grievance queue. Admin/HoD only, and a
// separate chunk so no plain teacher ever downloads it.
const ParentComplaintsPage = lazy(() => import("./pages/ParentComplaintsPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const OtpLoginPage = lazy(() => import("./pages/OtpLoginPage"));
const PendingApprovalPage = lazy(() => import("./pages/PendingApprovalPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
// Admin-issued one-tap sign-in. Public: the token in the URL fragment IS the
// credential, so this route must resolve before any auth check.
const MagicLinkPage = lazy(() => import("./pages/MagicLinkPage"));
const LiveClassesPage = lazy(() => import("./pages/LiveClassesPage"));
const LiveRoomPage = lazy(() => import("./pages/LiveRoomPage"));

function RequireAuth({ children }: { children: React.ReactNode }) {
  const loc = useLocation();
  if (!isAuthed()) return <Navigate to="/login" state={{ from: loc }} replace />;
  return <>{children}</>;
}

// Shown while a lazily-loaded route chunk is fetched. Chunks are precached by the
// service worker, so after the first visit this is effectively instant (offline too).
function PageFallback() {
  return (
    <div className="h-[100dvh] grid place-items-center bg-surface text-slate-600">
      <Loader2 size={20} className="animate-spin" />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      {/* LightboxProvider hosts the click-to-zoom image modal at the app root
          so every page can open student/question images in-place instead of
          punching out to a new tab. */}
      <LightboxProvider>
        <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          {/* One-time-code sign-in — pick your name, confirm last 4 digits. */}
          <Route path="/otp-login" element={<OtpLoginPage />} />
          {/* Public self-registration + forgot-password flows */}
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/pending" element={<PendingApprovalPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/magic" element={<MagicLinkPage />} />
          <Route path="/" element={<RequireAuth><DashboardPage /></RequireAuth>} />
          <Route path="/library" element={<RequireAuth><LibraryPage /></RequireAuth>} />
          <Route path="/create" element={<RequireAuth><CreatePage /></RequireAuth>} />
          <Route path="/profile" element={<RequireAuth><ProfilePage /></RequireAuth>} />
          <Route path="/attendance" element={<RequireAuth><AttendancePage /></RequireAuth>} />
          <Route path="/attendance/history" element={<RequireAuth><AttendanceHistoryPage /></RequireAuth>} />
          <Route path="/attendance/leaves" element={<RequireAuth><LeavesPage /></RequireAuth>} />
          <Route path="/attendance/checkins" element={<RequireAuth><AdminCheckInsPage /></RequireAuth>} />
          <Route path="/progress" element={<RequireAuth><ProgressPage /></RequireAuth>} />
          <Route path="/progress/period/:periodNo" element={<RequireAuth><PeriodLogPage /></RequireAuth>} />
          <Route path="/progress/school" element={<RequireAuth><SchoolProgressPage /></RequireAuth>} />
          {/* Routine builder. Role check lives in the page (admin|hod → else
              redirect home); the backend gates every write independently. */}
          <Route path="/routine" element={<RequireAuth><RoutinePage /></RequireAuth>} />
          <Route path="/notifications" element={<RequireAuth><NotificationsPage /></RequireAuth>} />
          {/* Read-open to all staff (recipients follow the push deep-link);
              the composer inside only renders for admin/HoD. */}
          {/* Announcements to PARENTS — admin + HoD. Staff notices (office →
              staff) are not routed here; pages/NoticesPage.tsx and its API
              block are kept, so bringing it back is a route and a menu item. */}
          <Route path="/announcements" element={<RequireAuth><AnnouncementsPage /></RequireAuth>} />
          {/* Taking the roll. Which classes a teacher may mark is decided by the
              server off their assignment matrix; the page renders whatever
              comes back, so there is no role check to keep in step here. */}
          <Route path="/student-attendance" element={<RequireAuth><StudentAttendancePage /></RequireAuth>} />
          <Route path="/student-attendance/register" element={<RequireAuth><StudentAttendanceRegisterPage /></RequireAuth>} />
          {/* Role check lives in the page (admin|hod|coordinator → else redirect
              home); every /rfid route is gated server-side independently. */}
          <Route path="/student-attendance/cards" element={<RequireAuth><RfidCardsPage /></RequireAuth>} />
          {/* Exam marks. Which papers a teacher may fill is decided by the
              server off exam_papers.assigned_to; the list renders whatever
              comes back, so there is no role check to keep in step here. */}
          <Route path="/exam-marks" element={<RequireAuth><ExamMarksPage /></RequireAuth>} />
          <Route path="/exam-marks/:paperId" element={<RequireAuth><ExamSheetPage /></RequireAuth>} />
          <Route path="/students" element={<RequireAuth><StudentParticipationPage /></RequireAuth>} />
          <Route path="/students/:studentId" element={<RequireAuth><StudentDossierPage /></RequireAuth>} />
          <Route path="/tests/:displayId/submissions" element={<RequireAuth><SubmissionsPage /></RequireAuth>} />
          <Route path="/tests/:displayId/submissions/:attemptId" element={<RequireAuth><AttemptPage /></RequireAuth>} />
          {/* Preview-as-student — read-only walkthrough of the test the way an
              enrolled student will see it, with no attempt row created. */}
          <Route path="/tests/:displayId/preview" element={<RequireAuth><PreviewPage /></RequireAuth>} />
          <Route path="/doubts" element={<RequireAuth><DoubtsListPage /></RequireAuth>} />
          <Route path="/doubts/:doubtId" element={<RequireAuth><DoubtThreadPage /></RequireAuth>} />
          <Route path="/complaints" element={<RequireAuth><ComplaintsRegistryPage /></RequireAuth>} />
          <Route path="/complaints/new" element={<RequireAuth><ComplaintCreatePage /></RequireAuth>} />
          {/* Parent doubts — the staff end of the Parent app's /ask screens.
              Without these routes, parents would be writing to nobody. */}
          <Route path="/parent-doubts" element={<RequireAuth><ParentDoubtsListPage /></RequireAuth>} />
          <Route path="/parent-doubts/:chatId" element={<RequireAuth><ParentDoubtThreadPage /></RequireAuth>} />
          {/* Parent complaints — admin/HoD. The API refuses everyone else, so
              the route stays mounted for all and the page simply shows the
              server's refusal rather than the menu lying about what exists. */}
          <Route path="/parent-complaints" element={<RequireAuth><ParentComplaintsPage /></RequireAuth>} />
          {/* Live classes. Who may go live for which section is decided by the
              server off teacher_class_assignments, so no role check here. */}
          <Route path="/live" element={<RequireAuth><LiveClassesPage /></RequireAuth>} />
          <Route path="/live/:id" element={<RequireAuth><LiveRoomPage /></RequireAuth>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </LightboxProvider>
    </BrowserRouter>
  );
}
